import { z } from 'zod';
import { KbLocale } from './kb.js';
import { Reason } from './platform-admin.js';

/**
 * Platform AI (docs/kb.md, "The API calls a model"): the few model calls Mandistry itself pays for on
 * the platform Anthropic key — Help Center answers, the help chat and the agent designer. A platform
 * admin picks the model and effort per purpose in the admin app; nothing here touches an org's providers.
 */
export const PLATFORM_AI_PURPOSES = ['helpAnswer', 'helpChat', 'agentDesigner'] as const;
export type PlatformAiPurpose = (typeof PLATFORM_AI_PURPOSES)[number];

export const PLATFORM_AI_EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type PlatformAiEffort = (typeof PLATFORM_AI_EFFORTS)[number];

/** USD per 1M tokens (Anthropic first-party, prompts ≤ 100K tokens). */
export interface PlatformAiPrice {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}

/**
 * How a model turns thinking off: Haiku 5.5 accepts `disabled` up to effort `high`, Sonnet 5.5 only
 * `between_tools` up to `high`, Opus 5.5 never (thinking always on; effort is the only control).
 */
export type ThinkingOff = 'disabled' | 'between_tools' | null;

export const PLATFORM_AI_MODELS = [
  {
    id: 'claude-haiku-5-5',
    label: 'Claude Haiku 5.5',
    price: { input: 0.1, output: 0.5, cacheRead: 0.01, cacheWrite: 0.125 },
    thinkingOff: 'disabled',
  },
  {
    id: 'claude-sonnet-5-5',
    label: 'Claude Sonnet 5.5',
    price: { input: 2, output: 10, cacheRead: 0.2, cacheWrite: 2.5 },
    thinkingOff: 'between_tools',
  },
  {
    id: 'claude-opus-5-5',
    label: 'Claude Opus 5.5',
    price: { input: 4, output: 20, cacheRead: 0.2, cacheWrite: 5 },
    thinkingOff: null,
  },
] as const satisfies readonly {
  id: string;
  label: string;
  price: PlatformAiPrice;
  thinkingOff: ThinkingOff;
}[];
export type PlatformAiModelId = (typeof PLATFORM_AI_MODELS)[number]['id'];
export const PLATFORM_AI_MODEL_IDS = PLATFORM_AI_MODELS.map((m) => m.id) as [
  PlatformAiModelId,
  ...PlatformAiModelId[],
];

export const platformAiModel = (id: string) => PLATFORM_AI_MODELS.find((m) => m.id === id);

/** Thinking stays off where the model allows it at this effort; otherwise the model decides (adaptive). */
export function thinkingOffFor(modelId: string, effort: PlatformAiEffort): ThinkingOff {
  // A model outside the catalog (custom AF_KB_MODEL) keeps the previous behaviour: thinking disabled.
  const m = platformAiModel(modelId);
  const off: ThinkingOff = m ? m.thinkingOff : 'disabled';
  return off && (effort === 'low' || effort === 'medium' || effort === 'high') ? off : null;
}

export const PlatformAiSetting = z.object({
  model: z.enum(PLATFORM_AI_MODEL_IDS),
  effort: z.enum(PLATFORM_AI_EFFORTS),
});
export type PlatformAiSetting = z.infer<typeof PlatformAiSetting>;

export const PlatformAiConfig = z.object({
  helpAnswer: PlatformAiSetting,
  helpChat: PlatformAiSetting,
  agentDesigner: PlatformAiSetting,
});
export type PlatformAiConfig = z.infer<typeof PlatformAiConfig>;

/** Cheapest capable default: Haiku at low effort (thinking off) for every purpose. */
export function defaultPlatformAiConfig(model: string = 'claude-haiku-5-5'): PlatformAiConfig {
  const m = platformAiModel(model)?.id ?? 'claude-haiku-5-5';
  return {
    helpAnswer: { model: m, effort: 'low' },
    helpChat: { model: m, effort: 'low' },
    agentDesigner: { model: m, effort: 'low' },
  };
}

export const PlatformAiConfigInput = z.object({ reason: Reason, config: PlatformAiConfig }).strict();

export interface PlatformAiConfigDto {
  config: PlatformAiConfig;
  defaults: PlatformAiConfig;
  /** False when no platform key is configured: every purpose answers "unavailable". */
  keyConfigured: boolean;
  dailyBudgetUsd: number;
  spendTodayUsd: number;
  models: { id: string; label: string; price: PlatformAiPrice }[];
  updatedAt: string | null;
}

// ---------- agent designer ----------

/** Template ids the designer may pick; tools, policies and guardrails always come from the template. */
export const AGENT_TEMPLATE_IDS = [
  'manager',
  'researcher',
  'developer',
  'assistant',
  'writer',
  'reviewer',
] as const;
export type AgentTemplateId = (typeof AGENT_TEMPLATE_IDS)[number];

export const AgentDraftInput = z.object({
  description: z.string().trim().min(8).max(2000),
  locale: KbLocale.default('en'),
});
export type AgentDraftInput = z.infer<typeof AgentDraftInput>;

/** What the model returns: profile text only — never tools, permissions or limits. */
export const AgentDraft = z.object({
  templateId: z.enum(AGENT_TEMPLATE_IDS),
  name: z.string().trim().min(1).max(60),
  roleTitle: z.string().trim().min(1).max(80),
  description: z.string().trim().min(1).max(300),
  instructions: z.string().trim().min(1).max(6000),
  firstTask: z.string().trim().max(500),
});
export type AgentDraft = z.infer<typeof AgentDraft>;

export type AgentDraftResult =
  | { ok: true; draft: AgentDraft; costUsd: number; model: string }
  | { ok: false; reason: 'no_key' | 'rate_limited' | 'budget' | 'error' };

// ---------- instruction coach ----------

/** Rewrite an agent's instructions on request; the human reviews and saves a new version. */
export const ImproveInstructionsInput = z.object({
  instructions: z.string().trim().min(20).max(20_000),
  /** What should change ("shorter", "ask before sending mail", …); empty = general clean-up. */
  goal: z.string().trim().max(1000).default(''),
  locale: KbLocale.default('en'),
});
export type ImproveInstructionsInput = z.infer<typeof ImproveInstructionsInput>;

export const ImprovedInstructions = z.object({
  instructions: z.string().trim().min(1).max(50_000),
  changes: z.array(z.string().trim().min(1).max(300)).max(8),
});
export type ImprovedInstructions = z.infer<typeof ImprovedInstructions>;

export type ImproveInstructionsResult =
  | ({ ok: true; costUsd: number; model: string } & ImprovedInstructions)
  | { ok: false; reason: 'no_key' | 'rate_limited' | 'budget' | 'error' };
